/**
 * Translations for LUKHAS iD
 *
 * Bilingual support: English and Spanish
 */

export const translations = {
  header: {
    signIn: { en: 'Sign In', es: 'Iniciar Sesión' },
    createAiD: { en: 'Create ΛiD', es: 'Crear ΛiD' },
  },

  hero: {
    skipToContent: { en: 'Skip to main content', es: 'Ir al contenido principal' },
    title1: { en: 'One Identity.', es: 'Una Identidad.' },
    title2: { en: 'Across Every Surface.', es: 'En Cada Superficie.' },
    subtitle: {
      en: 'ΛiD is your passkey-native, zero-knowledge identity across the LUKHAS constellation. One sign-in, scoped access everywhere.',
      es: 'ΛiD es tu identidad nativa de passkeys y conocimiento cero en la constelación LUKHAS. Un inicio de sesión, acceso delimitado en todas partes.',
    },
    ctaCreate: { en: 'Create Your ΛiD', es: 'Crea Tu ΛiD' },
  },

  whatIsAiD: {
    sectionLabel: { en: 'What is ΛiD', es: 'Qué es ΛiD' },
    title1: { en: 'Your Unified', es: 'Tu Identidad' },
    title2: { en: 'Digital Identity', es: 'Digital Unificada' },
    description: {
      en: 'ΛiD sits between you and every LUKHAS surface as a cryptographically anchored identity. Sign in once with passkeys or magic links, then share only the slices of yourself each app actually needs.',
      es: 'ΛiD se sitúa entre tú y cada superficie LUKHAS como una identidad criptográficamente anclada. Inicia sesión una vez con passkeys o enlaces mágicos, luego comparte solo las partes de ti que cada app realmente necesita.',
    },
    features: {
      zeroKnowledge: {
        label: { en: 'Zero-Knowledge', es: 'Conocimiento Cero' },
        value: { en: 'We verify you without seeing your secrets', es: 'Te verificamos sin ver tus secretos' },
      },
      postQuantum: {
        label: { en: 'Post-Quantum Ready', es: 'Listo para Post-Cuántico' },
        value: { en: 'Cryptography designed for the next decade', es: 'Criptografía diseñada para la próxima década' },
      },
      singleSignOn: {
        label: { en: 'LUKHAS SSO', es: 'SSO LUKHAS' },
        value: { en: 'One ΛiD across the constellation', es: 'Un ΛiD en toda la constelación' },
      },
    },
  },

  namespaces: {
    sectionLabel: { en: 'Identity Namespaces', es: 'Espacios de Identidad' },
    title1: { en: 'Six Dimensions', es: 'Seis Dimensiones' },
    title2: { en: 'of ΛiD', es: 'de ΛiD' },
    description: {
      en: 'Your ΛiD separates what you share into six namespaces, so you can grant access by layer instead of exposing your whole self.',
      es: 'Tu ΛiD separa lo que compartes en seis espacios, para que puedas otorgar acceso por capa en lugar de exponer todo tu ser.',
    },
    core: {
      name: { en: 'Core', es: 'Núcleo' },
      description: {
        en: 'Verified identity attributes and credentials',
        es: 'Atributos y credenciales de identidad verificados',
      },
    },
    behavioral: {
      name: { en: 'Behavioral', es: 'Conductual' },
      description: {
        en: 'How you interact with systems - patterns, not raw surveillance',
        es: 'Cómo interactúas con los sistemas - patrones, no vigilancia bruta',
      },
    },
    relational: {
      name: { en: 'Relational', es: 'Relacional' },
      description: {
        en: 'Your trusted graph and connections, scoped by consent',
        es: 'Tu grafo de confianza y conexiones, delimitado por consentimiento',
      },
    },
    contextual: {
      name: { en: 'Contextual', es: 'Contextual' },
      description: {
        en: 'Context-specific identities for each app or domain',
        es: 'Identidades específicas de contexto para cada app o dominio',
      },
    },
    preference: {
      name: { en: 'Preference', es: 'Preferencias' },
      description: {
        en: 'Choices and settings you can reset at any time',
        es: 'Opciones y ajustes que puedes restablecer en cualquier momento',
      },
    },
    temporal: {
      name: { en: 'Temporal', es: 'Temporal' },
      description: {
        en: 'Time-scoped history that can decay, prune, or reset',
        es: 'Historial temporal que puede decaer, podarse o reiniciarse',
      },
    },
  },

  security: {
    sectionLabel: { en: 'Security', es: 'Seguridad' },
    title1: { en: 'Security', es: 'Seguridad' },
    title2: { en: 'By Design', es: 'Por Diseño' },
    description: {
      en: 'Your ΛiD is protected by passkeys, modern cryptography, and a zero-knowledge architecture designed to be audited, not just marketed.',
      es: 'Tu ΛiD está protegido por passkeys, criptografía moderna y una arquitectura de conocimiento cero diseñada para ser auditada, no solo publicitada.',
    },
    features: {
      webauthn: { en: 'WebAuthn/FIDO2 Passkeys', es: 'Passkeys WebAuthn/FIDO2' },
      argon2: { en: 'Argon2id Key Derivation', es: 'Derivación de Claves Argon2id' },
      aes: { en: 'AES-256-GCM Encryption', es: 'Cifrado AES-256-GCM' },
      zeroKnowledge: { en: 'Zero-Knowledge Architecture', es: 'Arquitectura Conocimiento Cero' },
    },
  },

  cta: {
    title: { en: 'Ready to Own Your Identity?', es: '¿Listo para Poseer Tu Identidad?' },
    subtitle: {
      en: 'Create your ΛiD in minutes. Start with passkeys and grow with the LUKHAS constellation.',
      es: 'Crea tu ΛiD en minutos. Comienza con passkeys y crece con la constelación LUKHAS.',
    },
  },

  aidCard: {
    sectionLabel: { en: 'ΛiD Card', es: 'Tarjeta ΛiD' },
    badge: { en: 'Coming Q2 2026', es: 'Disponible Q2 2026' },
    title1: { en: 'Your ΛiD,', es: 'Tu ΛiD,' },
    title2: { en: 'Always With You.', es: 'Siempre Contigo.' },
    description: {
      en: 'Your identity, instantly accessible. Tap your phone to verify, share only what you choose, and stay in control - all from your Apple Wallet or Google Wallet.',
      es: 'Tu identidad, accesible al instante. Toca tu teléfono para verificar, comparte solo lo que elijas y mantén el control - todo desde tu Apple Wallet o Google Wallet.',
    },
    features: {
      wallet: {
        label: { en: 'Tap to Verify', es: 'Toca para Verificar' },
        value: { en: 'Hold your phone near any ΛiD reader. Instant, secure verification without showing your full identity.', es: 'Acerca tu teléfono a cualquier lector ΛiD. Verificación instantánea y segura sin mostrar tu identidad completa.' },
      },
      qrg: {
        label: { en: 'Dynamic Codes', es: 'Códigos Dinámicos' },
        value: { en: 'Every code refreshes automatically. Share it once, it expires immediately - no screenshots, no replays.', es: 'Cada código se actualiza automáticamente. Compártelo una vez, expira inmediatamente - sin capturas, sin repeticiones.' },
      },
      sso: {
        label: { en: 'One-Tap Sign In', es: 'Inicio con Un Toque' },
        value: { en: 'Approve logins from your phone. See who is asking, what they want, and decide in one tap.', es: 'Aprueba inicios de sesión desde tu teléfono. Ve quién pregunta, qué quiere, y decide con un toque.' },
      },
      selective: {
        label: { en: 'Share What You Choose', es: 'Comparte Lo Que Elijas' },
        value: { en: 'Prove your age without revealing your birthday. Verify your identity without exposing everything.', es: 'Prueba tu edad sin revelar tu cumpleaños. Verifica tu identidad sin exponer todo.' },
      },
    },
    comingSoon: {
      en: 'ΛiD Card launches Q2 2026 with Apple Wallet, Google Wallet, and NFC support.',
      es: 'La tarjeta ΛiD se lanza en Q2 2026 con soporte para Apple Wallet, Google Wallet y NFC.',
    },
  },

  walletFeatures: {
    sectionLabel: { en: 'ΛiD Wallet', es: 'Cartera ΛiD' },
    title1: { en: 'Your Identity,', es: 'Tu Identidad,' },
    title2: { en: 'Your Control.', es: 'Tu Control.' },
    description: {
      en: 'The ΛiD Wallet Pass puts you in charge. Share only what you want, when you want, with who you want.',
      es: 'El Pase de Cartera ΛiD te pone al mando. Comparte solo lo que quieras, cuando quieras, con quien quieras.',
    },
    privacy: {
      label: { en: 'Privacy First', es: 'Privacidad Primero' },
      value: { en: 'Your data stays on your device. We verify claims without seeing your secrets.', es: 'Tus datos permanecen en tu dispositivo. Verificamos afirmaciones sin ver tus secretos.' },
    },
    portable: {
      label: { en: 'Works Everywhere', es: 'Funciona en Todas Partes' },
      value: { en: 'Apple Wallet. Google Wallet. NFC readers. One identity across all your devices.', es: 'Apple Wallet. Google Wallet. Lectores NFC. Una identidad en todos tus dispositivos.' },
    },
    revocable: {
      label: { en: 'Instant Control', es: 'Control Instantáneo' },
      value: { en: 'Lost your phone? Revoke access in seconds. Your identity is always under your control.', es: '¿Perdiste tu teléfono? Revoca el acceso en segundos. Tu identidad siempre bajo tu control.' },
    },
    enterprise: {
      label: { en: 'Enterprise Ready', es: 'Listo para Empresas' },
      value: { en: 'Issue credentials to your team. Verify access at doors, devices, and digital services.', es: 'Emite credenciales a tu equipo. Verifica el acceso en puertas, dispositivos y servicios digitales.' },
    },
    stats: {
      nfc: { en: 'NFC Tap', es: 'Toque NFC' },
      nfcValue: { en: 'Instant', es: 'Instantáneo' },
      refresh: { en: 'Code Refresh', es: 'Actualización' },
      refreshValue: { en: 'Every 60s', es: 'Cada 60s' },
      platforms: { en: 'Platforms', es: 'Plataformas' },
      platformsValue: { en: 'iOS + Android', es: 'iOS + Android' },
    },
  },

  login: {
    pageTitle: { en: 'Sign In', es: 'Iniciar Sesión' },
    subtitle: { en: 'Sign in to your consciousness signature', es: 'Inicia sesión en tu firma de consciencia' },
    form: {
      emailLabel: { en: 'Email Address', es: 'Correo Electrónico' },
      emailPlaceholder: { en: 'your@email.com', es: 'tu@email.com' },
      passwordLabel: { en: 'Password', es: 'Contraseña' },
      passwordPlaceholder: { en: '••••••••', es: '••••••••' },
      rememberDevice: { en: 'Remember this device', es: 'Recordar este dispositivo' },
      forgotPassword: { en: 'Forgot password?', es: '¿Olvidaste tu contraseña?' },
      submitButton: { en: 'Sign In', es: 'Iniciar Sesión' },
      signingIn: { en: 'Signing in...', es: 'Iniciando sesión...' },
    },
    dividers: {
      orContinueWith: { en: 'Or continue with', es: 'O continuar con' },
      alternativeMethods: { en: 'Alternative methods', es: 'Métodos alternativos' },
    },
    passkey: {
      signInWithPasskey: { en: 'Sign in with Passkey', es: 'Iniciar sesión con Passkey' },
      continueWithFaceId: { en: 'Continue with Face ID', es: 'Continuar con Face ID' },
      continueWithTouchId: { en: 'Continue with Touch ID', es: 'Continuar con Touch ID' },
      continueWithBiometrics: { en: 'Continue with Biometrics', es: 'Continuar con Biometría' },
    },
    wallet: {
      signInWithAidCard: { en: 'Use LUKHΛS Pass', es: 'Usar Tarjeta LUKHΛS' },
      hint: { en: 'Scan the QR with your Apple or Google Wallet pass to approve sign-in.', es: 'Escanea el código QR con tu tarjeta Apple o Google Wallet para aprobar el inicio de sesión.' },
      qrExpires: { en: 'Expires in', es: 'Expira en' },
      qrWaiting: { en: 'Waiting for phone scan…', es: 'Esperando escaneo del teléfono…' },
      qrApproved: { en: 'Approved — signing you in', es: 'Aprobado — iniciando sesión' },
      addToAppleWallet: { en: 'Add to Apple Wallet', es: 'Añadir a Apple Wallet' },
      addToGoogleWallet: { en: 'Save to Google Wallet', es: 'Guardar en Google Wallet' },
    },
    device: {
      approveFromDevice: { en: 'Approve from another device', es: 'Aprobar desde otro dispositivo' },
      hint: { en: 'Open LUKHΛS on your phone and scan this code, or visit your saved link.', es: 'Abre LUKHΛS en tu teléfono y escanea este código, o visita tu enlace guardado.' },
      phoneWillAsk: { en: 'Your phone will ask for:', es: 'Tu teléfono te pedirá:' },
      challenges: {
        biometric: { en: 'Face ID / Touch ID', es: 'Face ID / Touch ID' },
        symbol: { en: 'Constellation symbol', es: 'Símbolo de constelación' },
        wallet_code: { en: '6-digit pass code', es: 'Código de 6 dígitos' },
        pattern: { en: 'Tap pattern', es: 'Patrón de toque' },
      },
    },
    oauth: {
      google: { en: 'Google', es: 'Google' },
      apple: { en: 'Apple', es: 'Apple' },
      signInWithApple: { en: 'Sign in with Apple', es: 'Iniciar sesión con Apple' },
      continueWithApple: { en: 'Continue with Apple', es: 'Continuar con Apple' },
      github: { en: 'GitHub', es: 'GitHub' },
    },
    register: {
      noAccount: { en: "Don't have a ΛiD?", es: '¿No tienes un ΛiD?' },
      createNow: { en: 'Create one now', es: 'Crea uno ahora' },
    },
    sso: {
      info: { en: 'Single sign-on across all LUKHAS services', es: 'Inicio de sesión único en todos los servicios LUKHAS' },
    },
  },

  register: {
    form: {
      emailHint: {
        en: 'Your account email - a unique ΛiD will be generated and used across the LUKHAS constellation',
        es: 'Tu correo de cuenta - se generará un ΛiD único que se usará en toda la constelación LUKHAS',
      },
    },
  },

  onboarding: {
    steps: {
      step3: {
        main: { en: 'Secure.', es: 'Seguro.' },
        sub: { en: 'Bind ΛiD to your device with passkeys - ready for wallet and QRG.', es: 'Vincula ΛiD a tu dispositivo con passkeys - listo para wallet y QRG.' },
      },
      step4: {
        main: { en: 'Understand.', es: 'Comprende.' },
        sub: { en: 'Six namespaces. One ΛiD. You choose what each surface sees.', es: 'Seis espacios. Un ΛiD. Tú eliges lo que cada superficie ve.' },
      },
    },
  },

  dashboard: {
    pageTitle: { en: 'ΛiD Overview', es: 'Resumen de ΛiD' },
    loading: { en: 'Loading...', es: 'Cargando...' },
    tabs: {
      identity:    { en: 'ΛiD Card',           es: 'Tarjeta ΛiD'          },
      wallet:      { en: 'Identity Pass',       es: 'Tarjeta de Identidad' },
      security:    { en: 'Security & Sessions', es: 'Seguridad y Sesiones' },
      connections: { en: 'Where ΛiD is Used',  es: 'Dónde se Usa ΛiD'    },
    },
    identity: {
      yourIdentifier: { en: 'Your ΛiD Number', es: 'Tu Número ΛiD' },
      defaultId: { en: 'ΛID-000000', es: 'ΛID-000000' },
      account: { en: 'Account', es: 'Cuenta' },
      memberSince: { en: 'Member since', es: 'Miembro desde' },
      accountType: { en: 'Account type', es: 'Tipo de cuenta' },
      personal: { en: 'Personal', es: 'Personal' },
      email: { en: 'Email', es: 'Correo' },
    },
    security: {
      authentication: { en: 'Authentication', es: 'Autenticación' },
      passphrase: {
        title: { en: 'Passphrase', es: 'Frase de Paso' },
        description: { en: 'Your primary authentication', es: 'Tu autenticación principal' },
      },
      passkey: {
        title: { en: 'Passkey', es: 'Passkey' },
      },
      activeSessions: { en: 'Active Sessions', es: 'Sesiones Activas' },
      currentSession: { en: 'Current session', es: 'Sesión actual' },
      browser: {
        chrome: { en: 'Chrome', es: 'Chrome' },
        safari: { en: 'Safari', es: 'Safari' },
        firefox: { en: 'Firefox', es: 'Firefox' },
        other: { en: 'Browser', es: 'Navegador' },
      },
      accountActions: { en: 'Account Actions', es: 'Acciones de Cuenta' },
      exportData: { en: 'Export your data', es: 'Exportar tus datos' },
    },
    wallet: {
      sectionLabel: { en: 'Identity Pass', es: 'Tarjeta de Identidad' },
      description: {
        en: 'Carry your LUKHΛS identity in Apple or Google Wallet. Scan the pass barcode to approve sign-ins on any device.',
        es: 'Lleva tu identidad LUKHΛS en Apple o Google Wallet. Escanea el código de barras para aprobar inicios de sesión en cualquier dispositivo.',
      },
      addToApple: { en: 'Add to Apple Wallet', es: 'Añadir a Apple Wallet' },
      saveToGoogle: { en: 'Save to Google Wallet', es: 'Guardar en Google Wallet' },
      passDetails: { en: 'Pass details', es: 'Detalles de tarjeta' },
      validThrough: { en: 'Valid through', es: 'Válido hasta' },
      noPass: {
        en: 'No pass linked to this account yet.',
        es: 'Aún no hay tarjeta vinculada a esta cuenta.',
      },
    },
    connections: {
      ecosystem: { en: 'LUKHAS Ecosystem', es: 'Ecosistema LUKHAS' },
      services: {
        lukhasAi: {
          name: { en: 'lukhas.ai', es: 'lukhas.ai' },
          description: { en: 'AI Platform', es: 'Plataforma de IA' },
        },
        lukhasDev: {
          name: { en: 'lukhas.dev', es: 'lukhas.dev' },
          description: { en: 'Developer Portal', es: 'Portal de Desarrolladores' },
        },
        lukhasCloud: {
          name: { en: 'lukhas.cloud', es: 'lukhas.cloud' },
          description: { en: 'Cloud Services', es: 'Servicios Cloud' },
        },
      },
      ecosystemNote: { en: 'Your ΛiD works across the entire LUKHAS constellation.', es: 'Tu ΛiD funciona en toda la constelación LUKHAS.' },
    },
  },

  footer: {
    product: { en: 'Product', es: 'Producto' },
    security: { en: 'Security', es: 'Seguridad' },
    privacy: { en: 'Privacy', es: 'Privacidad' },
    enterprise: { en: 'Enterprise', es: 'Empresas' },
    developers: { en: 'Developers', es: 'Desarrolladores' },
    documentation: { en: 'Documentation', es: 'Documentación' },
    api: { en: 'API Reference', es: 'Referencia API' },
    integration: { en: 'Integration', es: 'Integración' },
    company: { en: 'Company', es: 'Compañía' },
    about: { en: 'About', es: 'Acerca de' },
    careers: { en: 'Careers', es: 'Carreras' },
    contact: { en: 'Contact', es: 'Contacto' },
    legal: { en: 'Legal', es: 'Legal' },
    terms: { en: 'Terms', es: 'Términos' },
    cookies: { en: 'Cookies', es: 'Cookies' },
    ecosystem: { en: 'LUKHAS Ecosystem', es: 'Ecosistema LUKHAS' },
    aiPlatform: { en: 'AI Platform', es: 'Plataforma de IA' },
    devPortal: { en: 'Developer Portal', es: 'Portal de Desarrolladores' },
    ioServices: { en: 'IO Services', es: 'Servicios IO' },
    identity: { en: 'Identity', es: 'Identidad' },
    cloudPlatform: { en: 'Cloud Platform', es: 'Plataforma Cloud' },
    teamCollab: { en: 'Team Collaboration', es: 'Colaboración en Equipo' },
  },

  a11y: {
    accountActions: { en: 'Account actions', es: 'Acciones de cuenta' },
    footerNav: { en: 'Footer navigation', es: 'Navegación del pie de página' },
  },
} as const

export type Translations = typeof translations
